import CalendarMonth from "@mui/icons-material/CalendarMonth";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { type ListMode } from "components/Tasks/ListModeButton";
import {
  CALENDAR_FEED_LOOKAHEAD,
  CALENDAR_FEED_LOOKBACK,
} from "encamp-shared/src/tasks/calendarFeedSearch";
import { TaskCalendarScope } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { Duration } from "luxon";

import { CalendarFilterBuilder } from "./CalendarFilterBuilder";
import { CalendarPreviewList } from "./CalendarPreviewList";
import { CalendarSubscriptionUrlSection } from "./CalendarSubscriptionUrlSection";
import { useCalendarSubscription } from "./useCalendarSubscription";
import { useCalendarSubscriptionDraft } from "./useCalendarSubscriptionDraft";
import { useTaskCalendarPreview } from "./useTaskCalendarPreview";

const SCOPE_LABELS: Record<TaskCalendarScope, string> = {
  [TaskCalendarScope.All]: "All tasks I can see",
  [TaskCalendarScope.Mine]: "Assigned to me or watching",
  [TaskCalendarScope.Custom]: "Custom filter",
};

const SCOPE_DESCRIPTIONS: Record<TaskCalendarScope, string> = {
  [TaskCalendarScope.All]: "Every task at the facilities you have access to.",
  [TaskCalendarScope.Mine]: "Only tasks where you're the assignee or a watcher.",
  [TaskCalendarScope.Custom]: "Only tasks that match the filters below.",
};

const SCOPES = [TaskCalendarScope.All, TaskCalendarScope.Mine, TaskCalendarScope.Custom];

const FEED_WINDOW_NOTE =
  `Your calendar includes tasks due from ` +
  `${Duration.fromObject(CALENDAR_FEED_LOOKBACK).toHuman()} ago through ` +
  `${Duration.fromObject(CALENDAR_FEED_LOOKAHEAD).toHuman()} ahead, ` +
  "and moves forward as time passes.";

type TaskCalendarSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The task list's layout; the preview follows it. */
  listMode: ListMode;
};

export const TaskCalendarSubscriptionDialog: React.FC<TaskCalendarSubscriptionDialogProps> = ({
  open,
  onClose,
  listMode,
}) => {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { subscription, loading, saving, revoking, save, revokeUrl } = useCalendarSubscription();
  const isMutating = saving || revoking;

  const draft = useCalendarSubscriptionDraft(subscription);
  const { scope: selectedScope, isCustom, customSearch, changed, missingFilter } = draft;
  const [previewSearch] = useDebounce(customSearch, 400);

  const preview = useTaskCalendarPreview({
    scope: selectedScope,
    search: isCustom ? previewSearch : null,
    listMode,
    skip: isCustom && !previewSearch,
  });

  const runSave = async (successMessage: string) => {
    try {
      await save({ scope: selectedScope, search: isCustom ? customSearch : null });
      draft.reset();
      alerts.success(successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      alerts.error(`Failed to save calendar subscription: ${message}`);
    }
  };

  const handleRevoke = async () => {
    try {
      const revoked = await revokeUrl();
      if (revoked) {
        draft.reset();
        alerts.success("Calendar subscription revoked");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      alerts.error(`Failed to revoke: ${message}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="close" onClick={onClose}>
          {changed ? "Cancel" : "Close"}
        </Button>,
        <Button
          key="save"
          variant="contained"
          onClick={() =>
            runSave("Saved. Your calendar app will pick up the change on its next refresh.")
          }
          disabled={!subscription || !changed || missingFilter || isMutating}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save
        </Button>,
      ]}
      title={
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonth color="primary" />
          <Typography variant="h6">Calendar Subscription</Typography>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Subscribe to your tasks in your favorite calendar app (Google Calendar, Apple Calendar,
            Outlook, etc.). Your calendar will automatically sync with your tasks in Encamp.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {FEED_WINDOW_NOTE}
          </Typography>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label="What tasks should appear on your external calendar?"
                  value={selectedScope}
                  onChange={(event) => {
                    const scope = SCOPES.find((s) => s === event.target.value);
                    if (scope) draft.setScope(scope);
                  }}
                  helperText={SCOPE_DESCRIPTIONS[selectedScope]}
                >
                  {SCOPES.map((scope) => (
                    <MenuItem key={scope} value={scope}>
                      {SCOPE_LABELS[scope]}
                      {subscription && scope === draft.savedScope ? " (current)" : ""}
                    </MenuItem>
                  ))}
                </TextField>
                {isCustom && tenantId && (
                  <CalendarFilterBuilder
                    tenantId={tenantId}
                    value={draft.filter}
                    onChange={draft.setFilter}
                  />
                )}
              </Stack>
              {missingFilter ? (
                <Typography variant="body2" color="text.secondary">
                  Choose at least one filter.
                </Typography>
              ) : (
                <CalendarPreviewList {...preview} />
              )}
            </Box>

            <CalendarSubscriptionUrlSection
              url={subscription?.url ?? null}
              saving={saving}
              disabled={isMutating}
              canGenerate={!missingFilter}
              onGenerate={() => runSave("Calendar subscription URL generated")}
              onRevoke={handleRevoke}
            />
          </>
        )}
      </Stack>
    </Dialog>
  );
};
